/** The progress chart's geometry, drawn at about the size it is shown so its labels stay at text size. */
import { monthDay } from "./days.js";
import type { ProgressPoint } from "./progress.js";
import { weightNumber } from "./sets.js";

function niceStep(raw: number): number {
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const step of [1, 2, 2.5, 5, 10]) if (step * p >= raw) return step * p;
  return 10 * p;
}

export interface ChartGeometry {
  w: number;
  h: number;
  padL: number;
  padR: number;
  padB: number;
  grid: { y: number; label: string }[];
  ticks: { x: number; label: string }[];
  dots: { x: number; y: number; last: boolean }[];
  line: string | null;
  area: string | null;
  value: { x: number; y: number; anchor: "middle" | "end"; text: string };
}

export function chartWidth(viewWidth: number | null | undefined): number {
  return Math.max(320, Math.min(1100, viewWidth ? viewWidth - 110 : 640));
}

export function chartGeometry(points: ProgressPoint[], unit: string, w: number): ChartGeometry {
  const h = w < 600 ? 200 : 240;
  const padL = 44;
  const padR = 18;
  const padT = 26;
  const padB = 30;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = Math.max(max - min, max * 0.1, 5);
  const step = niceStep((spread * 1.3) / 4);
  const lo = Math.max(0, Math.floor((min - spread * 0.15) / step) * step);
  let hi = Math.ceil((max + spread * 0.15) / step) * step;
  if (hi <= lo) hi = lo + step;
  const x = (i: number) => (points.length === 1 ? (padL + w - padR) / 2 : padL + (i * (w - padL - padR)) / (points.length - 1));
  const y = (v: number) => padT + ((hi - v) * (h - padT - padB)) / (hi - lo);
  const r1 = (n: number) => Number(n.toFixed(1));

  const grid: ChartGeometry["grid"] = [];
  for (let v = lo; v <= hi + step / 2; v += step) grid.push({ y: r1(y(v)), label: weightNumber(v) });

  const ticks: ChartGeometry["ticks"] = [];
  const every = Math.ceil(points.length / 7);
  points.forEach((p, i) => {
    const last = points.length - 1;
    if (i % every === 0 || i === last) {
      if (i !== last && last - i < every) return;
      ticks.push({ x: r1(x(i)), label: monthDay(p.week) });
    }
  });

  const path = points.map((p, i) => (i ? "L" : "M") + x(i).toFixed(1) + "," + y(p.value).toFixed(1)).join("");
  const n = points.length;
  const lastP = points[n - 1]!;
  const lx = x(n - 1);
  return {
    w,
    h,
    padL,
    padR,
    padB,
    grid,
    ticks,
    dots: points.map((p, i) => ({ x: r1(x(i)), y: r1(y(p.value)), last: i === n - 1 })),
    line: n > 1 ? path : null,
    area: n > 1 ? path + "L" + x(n - 1).toFixed(1) + "," + (h - padB) + "L" + x(0).toFixed(1) + "," + (h - padB) + "Z" : null,
    value: { x: r1(n === 1 ? lx : lx - 8), y: r1(y(lastP.value) - 10), anchor: n === 1 ? "middle" : "end", text: weightNumber(lastP.value) + " " + unit },
  };
}
